import { getTableDateOrWeekday } from "../Utils/DateTime";
import { getContractsArr, getThreadTypesArr, getAllUsersArr } from "../Store/Reducers";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { getColorClass, getTaskColor, extractObserverUserIDs, findUserByIDOrMobile } from "../Utils/Utilities";
import { useAllConnectionsFromIDB, useAllThreadsFromIDB } from "./useConnectionData";

/* REPLACEMENT: safer, exported hook (keeps same shape of returned taskData) */
export const useTaskByID = (taskID) => {
  const { data: tasks = [], loading: tasksLoading = false } = useSelector((state) => state.taskData || {});
  const users = useSelector(getAllUsersArr) || [];
  const threadTypes = useSelector(getThreadTypesArr) || [];
  const contractsArr = useSelector(getContractsArr) || [];

  // A task can be linked to a connection/thread from ANY of the user's agencies, but
  // state.connectionData/state.threadData are scoped to the currently active agency only
  // (see reloadAgencyData in Utils/AgencySwitch.js). Load the full, unfiltered
  // set straight from IndexedDB so cross-agency links resolve instead of showing blank.
  const allConnections = useAllConnectionsFromIDB(tasks);
  const allThreads = useAllThreadsFromIDB(tasks);

  const loading = tasksLoading;

  const taskData = useMemo(() => {
    if (!taskID || loading) return null;

    const task = tasks.find(t => (t.TaskID?.toString() === taskID?.toString()));
    if (!task) return null;

    const connection = allConnections.find(c => c.ConnID?.toString() === task.ConnID?.toString());
    const taskMobile = task.CreatorMobile || task.creatorMobile || task.CreatedMobile || task.POCMobile || task.pocMobile || task.Mobile || task.mobile || null;
    const executive = findUserByIDOrMobile(users, task.POCUserID, task.POCMobile || task.pocMobile || taskMobile);
    const creator = findUserByIDOrMobile(users, task.CreatedUserID, task.CreatorMobile || task.creatorMobile || task.CreatedMobile || taskMobile);

    const linkedThreads = (task.ThreadIDs || '')
      .split(',')
      .map(id => {
        const tid = id?.trim();
        if (!tid) return null;
        const th = allThreads.find(x => x.ThreadID?.toString() === tid);
        if (!th) return null;
        return {
          threadID: th.ThreadID,
          name: th.MainProduct || th.Product || `Thread #${th.ThreadID}`,
          typeID: th.TypeID,
          dueDT: th.DueDT || th.targetDT || null,
          sinceDT: th.SinceDT || th.sinceDT || null,
        };
      })
      .filter(Boolean);

    const linkedCtrcs = (task.CtrcIDs || '').split(',').map(id => {
      const cid = id?.trim();
      if (!cid) return null;
      const th = contractsArr.find(x => x.ContractID?.toString() === cid);
      if (!th) return null;
      return {
        ctrcID: th.ContractID,
        type: th.ContractType || '',
        expiryDT: th.ExpiryDT || null,
        startDT: th.StartDT || null,
        isSeller: th.IsSeller || 0
      };
    }).filter(Boolean)

    return {
      taskID: task.TaskID,
      title: task.Task,
      description: task.Description,
      type: task.TaskType,
      priority: task.Priority,
      status: task.Status,
      dueDT: task.DueDT,
      dueDTF: getTableDateOrWeekday(task.DueDT),
      dueDTColor: getColorClass(getTaskColor(task.DueDT)),
      pocUserID: task.POCUserID,
      pocUserName: executive?.name || 'Not Assigned',
      connID: task.ConnID,
      threadIDs: task.ThreadIDs,
      ctrcIDs: task.CtrcIDs,
      connectionData: {
        bizName: connection?.BizName,
        contactName: connection?.ContactName,
        mobile: connection?.Mobile,
        email: connection?.Email,
        cc: connection?.CC || "91",
        contactCard: connection?.ContactCard,
      },
      linkedThreads,
      linkedCtrcs,
      scIDs: task.SecConnIDs,
      createdUserID: task?.CreatedUserID || 0,
      createdDT: task?.CreatedDT || null,
      createdUserName: creator?.name || 'Unknown',
      lastRmdrDT: task.LastRmdrDT || null,
      rmdrDT: task.RmdrDT || null,
      createdCtgID: task.CreatedCtgID || 0,
      pocCtgID: task.POCCtgID || 0,
      pocTask: task.POCTask || '',
      pocPriority: task.POCPriority || 'M',
      AgencyID: task.AgencyID || 0,
      canComplete: task.CanComplete == true,
      statusNote: task.StatusNote || '',
      statusDT: task.StatusDT || null,
      updatedDT: task.UpdatedDT || null,
      repeatAfter: Number(task.RepeatAfter) || 0,
      repeatDT: task.RepeatDT || '',
      taskClass: task.Class || 1,
      options: task.Options || null,
      observerUserIDs: extractObserverUserIDs(task).join(","),
      observerUActions: (() => {
        const u = task.ObserverUActions || task.observerUActions || task.obsUActions || task.ObsUActions;
        if (!u) return [];
        try {
          return typeof u === 'string' ? JSON.parse(u) : u;
        } catch (e) {
          console.error("Failed to parse observerUActions JSON:", e);
          return [];
        }
      })(),
      updates: (() => {
        const u = task.Updates || task.updates;
        if (!u) return [];
        try {
          return typeof u === 'string' ? JSON.parse(u) : u;
        } catch (e) {
          console.error("Failed to parse updates JSON:", e);
          return [];
        }
      })(),
    };
  }, [taskID, tasks, allConnections, allThreads, users, threadTypes, contractsArr, loading]);

  return { taskData, loading };
};
